import React, { useMemo } from 'react';
import classnames from 'classnames';
import { endOfDay, startOfDay } from 'date-fns';

import type {
	ClassName,
	TileClassNameFunc,
	TileContentFunc,
	TileDisabledFunc,
} from './shared/types';

type TileProps = {
	activeStartDate: Date;
	children: React.ReactNode;
	classes?: string[];
	date: Date;
	formatAbbr?: (locale: string, date: Date) => string;
	locale: string;
	maxDate?: Date;
	minDate?: Date;
	onClick?: (date: Date, event: React.MouseEvent<HTMLButtonElement>) => void;
	onMouseOver?: (date: Date) => void;
	style?: React.CSSProperties;
	tileClassName?: TileClassNameFunc | ClassName;
	tileContent?: TileContentFunc | React.ReactNode;
	tileDisabled?: TileDisabledFunc;
	tileAriaLabel?: (d: Date) => string;
};

export default function Tile(props: TileProps) {
	const {
		activeStartDate,
		children,
		classes,
		date,
		formatAbbr,
		locale,
		maxDate,
		minDate,
		onClick,
		onMouseOver,
		style,
		tileClassName: tileClassNameProps,
		tileContent: tileContentProps,
		tileDisabled,
		tileAriaLabel,
	} = props;

	const tileClassName = useMemo(() => {
		const args = { activeStartDate, date };

		return typeof tileClassNameProps === 'function' ? tileClassNameProps(args) : tileClassNameProps;
	}, [activeStartDate, date, tileClassNameProps]);

	const tileContent = useMemo(() => {
		const args = { activeStartDate, date };

		return typeof tileContentProps === 'function' ? tileContentProps(args) : tileContentProps;
	}, [activeStartDate, date, tileContentProps]);

	return (
		<button
			className={classnames(classes, tileClassName)}
			disabled={
				(minDate &&
					Math.floor(startOfDay(minDate).getTime() / 1000) > Math.floor(date.getTime() / 1000)) ||
				(maxDate &&
					Math.floor(endOfDay(maxDate).getTime() / 1000) < Math.floor(date.getTime() / 1000)) ||
				(tileDisabled && tileDisabled({ activeStartDate, date }))
			}
			onClick={onClick ? event => onClick(date, event) : undefined}
			onFocus={onMouseOver ? () => onMouseOver(date) : undefined}
			onMouseOver={onMouseOver ? () => onMouseOver(date) : undefined}
			style={style}
			type="button"
			aria-label={tileAriaLabel?.(date)}
		>
			{formatAbbr ? <abbr aria-label={formatAbbr(locale, date)}>{children}</abbr> : children}
			{tileContent}
		</button>
	);
}
